import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { GhostButton } from "~/components/MessageButtons";
import { useOptionalVerificationLauncher } from "~/components/VerificationLauncherContext";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import { recordSdkBreadcrumb } from "~/lib/telemetry";
import { usePortalPlacement } from "~/lib/portal-positioning";
import type { Client, MatrixDevice } from "~/lib/matrix";

interface DeviceVerificationLauncherProps {
  client: Client | null;
  userId: string;
  loadDevices: () => Promise<MatrixDevice[]>;
  label?: string;
  variant?: "compact" | "ghost";
  mode?: "verify-other" | "verify-self";
  currentDeviceId?: string | null;
}

const PICKER_WIDTH = 280;
const OFFSET = 8;

export function DeviceVerificationLauncher({
  client,
  userId,
  loadDevices,
  label = "Verify",
  variant = "compact",
  mode = "verify-other",
  currentDeviceId = null,
}: DeviceVerificationLauncherProps) {
  const launcher = useOptionalVerificationLauncher();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [picker, setPicker] = useState<MatrixDevice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [allVerified, setAllVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (deviceId: string) => {
    setBusy(true);
    setError(null);
    try {
      recordSdkBreadcrumb("matrix.verification", "user initiated SAS", null, {
        targetUserId: userId,
        deviceId,
      });
      await launcher?.startVerification(userId, deviceId);
      setPicker(null);
    } catch {
      setError("Could not start verification. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const open = async () => {
    if (!client || busy) return;
    setBusy(true);
    setAllVerified(false);
    setError(null);
    try {
      const devices = await loadDevices();
      if (mode === "verify-self" && !currentDeviceId) {
        setError("Could not identify this session. Try again after signing in.");
        return;
      }
      const targets =
        mode === "verify-self"
          ? devices.filter((device) => device.deviceId !== currentDeviceId)
          : devices.filter((device) => !device.isVerified);
      if (devices.length === 0) {
        setError("No sessions found. Try again later.");
        return;
      }
      if (mode === "verify-other" && targets.length === 0) {
        setAllVerified(true);
        return;
      }
      if (targets.length === 0) {
        setError("No other sessions found. Try again later.");
        return;
      }
      if (mode === "verify-other" && targets.length === 1) {
        await start(targets[0]!.deviceId);
        return;
      }
      setPicker(targets);
    } catch {
      setError("Could not load sessions. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!launcher) return null;

  const disabled = !client || busy || allVerified;
  const buttonLabel = busy ? "Starting" : label;

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      {variant === "ghost" ? (
        <GhostButton ref={buttonRef} onClick={() => void open()} disabled={disabled}>
          {buttonLabel}
        </GhostButton>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className="chat-button enabled:hover:text-[var(--chat-fg-primary)]"
          onClick={() => void open()}
          disabled={disabled}
          title={allVerified ? "All sessions verified" : undefined}
          style={{
            border: "none",
            borderRadius: "var(--chat-radius-sm)",
            background: "transparent",
            color: disabled ? "var(--chat-fg-muted)" : "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            padding: "var(--chat-space-1) var(--chat-space-2)",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          {buttonLabel}
        </button>
      )}
      {picker ? (
        <DevicePicker
          anchor={buttonRef.current}
          devices={picker}
          busy={busy}
          mode={mode}
          onSelect={(deviceId) => void start(deviceId)}
          onClose={() => setPicker(null)}
        />
      ) : null}
      {error ? (
        <span
          role="alert"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + var(--chat-space-1))",
            width: 220,
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            zIndex: 4,
          }}
        >
          {error}
        </span>
      ) : null}
    </span>
  );
}

function DevicePicker({
  anchor,
  devices,
  busy,
  mode,
  onSelect,
  onClose,
}: {
  anchor: HTMLElement | null;
  devices: MatrixDevice[];
  busy: boolean;
  mode: "verify-other" | "verify-self";
  onSelect: (deviceId: string) => void;
  onClose: () => void;
}) {
  const portalRoot = usePortalRoot();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const placement = usePortalPlacement({
    anchor,
    portalRoot,
    computeViewportCoords: (rect, surface) => {
      const maxLeft = surface.offsetLeft + surface.width - PICKER_WIDTH - OFFSET;
      const viewportLeft = Math.max(surface.offsetLeft + OFFSET, Math.min(rect.left, maxLeft));
      const viewportTop = Math.min(
        rect.bottom + OFFSET,
        surface.offsetTop + surface.height - OFFSET,
      );
      return { viewportTop, viewportLeft };
    },
  });

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest("[data-device-picker]")) return;
      if (anchor?.contains(target)) return;
      onClose();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchor, onClose]);

  useFocusTrap(dialogRef, placement !== null);

  if (!portalRoot || !placement) return null;

  return createPortal(
    <div
      ref={dialogRef}
      data-device-picker
      role="dialog"
      aria-label="Choose a session"
      style={{
        position: placement.position,
        top: placement.top,
        left: placement.left,
        width: PICKER_WIDTH,
        // Above the Dialog scrim (100): Settings opens this picker from inside a dialog.
        zIndex: 110,
        borderRadius: "var(--chat-radius-xl)",
        background: "var(--chat-surface-overlay)",
        border: "1px solid var(--chat-surface-border)",
        boxShadow: "var(--chat-shadow-popover)",
        padding: "var(--chat-space-2)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <div
        style={{
          padding: "var(--chat-space-1) var(--chat-space-2) var(--chat-space-2)",
          color: "var(--chat-fg-muted)",
          fontSize: "var(--chat-type-body-xs-size)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        Choose a session
      </div>
      {devices.map((device) => (
        <button
          key={device.deviceId}
          type="button"
          className="chat-button enabled:hover:bg-[var(--chat-surface-panel)]"
          disabled={busy || (mode === "verify-other" && device.isVerified)}
          onClick={() => onSelect(device.deviceId)}
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--chat-space-3)",
            border: "none",
            borderRadius: "var(--chat-radius-sm)",
            background: "transparent",
            padding: "var(--chat-space-2)",
            color:
              mode === "verify-other" && device.isVerified
                ? "var(--chat-fg-muted)"
                : "var(--chat-fg-primary)",
            cursor: busy || (mode === "verify-other" && device.isVerified) ? "default" : "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {device.displayName || device.deviceId}
            </span>
            <span
              style={{ color: "var(--chat-fg-muted)", fontSize: "var(--chat-type-body-xs-size)" }}
            >
              {device.deviceId}
            </span>
          </span>
          <span
            style={{ color: "var(--chat-fg-muted)", fontSize: "var(--chat-type-body-xs-size)" }}
          >
            {device.isVerified ? "Verified" : "Verify"}
          </span>
        </button>
      ))}
    </div>,
    portalRoot,
  );
}
